import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ConsultationBooking {
  id: number
  user_id: number
  agent_id: number | null
  consultation_type: string
  title: string
  description: string | null
  scheduled_at: string
  duration_minutes: number
  status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled'
  meeting_url: string | null
  meeting_notes: string | null
  timezone: string
  created_at: string
  user?: { username: string; first_name: string | null; last_name: string | null; email: string | null }
  agent?: { agent_name: string }
}

interface ConsultationAdminProps {
  adminUser?: any
}

const ConsultationAdmin: React.FC<ConsultationAdminProps> = () => {
  const [bookings, setBookings] = useState<ConsultationBooking[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBooking, setSelectedBooking] = useState<ConsultationBooking | null>(null)
  const [filterStatus, setFilterStatus] = useState('all')
  const [meetingUrl, setMeetingUrl] = useState('')
  const [meetingNotes, setMeetingNotes] = useState('')
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    loadBookings()
  }, [filterStatus])

  const loadBookings = async () => {
    setLoading(true)
    try {
      const filters: Record<string, any> = {};
      if (filterStatus !== 'all') {
        filters.status = filterStatus;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'select',
          data: {
            table: 'consultation_bookings',
            select: '*, user:user_id(username, first_name, last_name, email), agent:agent_id(agent_name)',
            filters: Object.keys(filters).length > 0 ? filters : undefined,
            order: [{ column: 'scheduled_at', ascending: true }]
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setBookings(result.data || [])
    } catch (e) {
      console.error('Error loading bookings:', e)
    } finally {
      setLoading(false)
    }
  }

  const updateBookingStatus = async (id: number, status: string, extras?: object) => {
    setUpdating(true)
    try {
      const updateData: any = { status, updated_at: new Date().toISOString(), ...extras }

      if (status === 'confirmed') {
        updateData.confirmed_at = new Date().toISOString()
      } else if (status === 'completed') {
        updateData.completed_at = new Date().toISOString()
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update',
          data: {
            table: 'consultation_bookings',
            updates: updateData,
            filters: { id }
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await loadBookings()
      setSelectedBooking(null)
    } catch (e) {
      console.error('Error updating booking:', e)
      alert('Failed to update booking')
    } finally {
      setUpdating(false)
    }
  }

  const handleConfirm = async (booking: ConsultationBooking) => {
    if (!meetingUrl.trim()) {
      alert('Please enter a meeting URL')
      return
    }
    await updateBookingStatus(booking.id, 'confirmed', { meeting_url: meetingUrl })
    setMeetingUrl('')
  }

  const handleComplete = async (booking: ConsultationBooking) => {
    await updateBookingStatus(booking.id, 'completed', { meeting_notes: meetingNotes })
    setMeetingNotes('')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-yellow-100 text-yellow-800'
      case 'confirmed': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-purple-100 text-purple-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getConsultationTypeLabel = (type: string) => {
    switch (type) {
      case 'investment_guidance': return '💰 Share Guidance'
      case 'business_opportunity': return '📈 Business Opportunity'
      case 'technical_support': return '🔧 Technical Support'
      default: return type
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled (Pending)</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span className="text-gray-600">{bookings.length} booking(s)</span>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : bookings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No consultation bookings found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Title</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Scheduled</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bookings.map(booking => (
                <tr key={booking.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {booking.user?.first_name || booking.user?.last_name
                        ? `${booking.user.first_name || ''} ${booking.user.last_name || ''}`.trim()
                        : booking.user?.username || `User ${booking.user_id}`}
                    </div>
                    <div className="text-sm text-gray-500">{booking.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{getConsultationTypeLabel(booking.consultation_type)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{booking.title}</div>
                    {booking.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{booking.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>{new Date(booking.scheduled_at).toLocaleDateString()}</div>
                    <div className="text-gray-500">{new Date(booking.scheduled_at).toLocaleTimeString()}</div>
                    <div className="text-sm text-white">{booking.duration_minutes} min</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        setSelectedBooking(booking)
                        setMeetingUrl(booking.meeting_url || '')
                        setMeetingNotes(booking.meeting_notes || '')
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Manage Consultation</h3>
              <button onClick={() => setSelectedBooking(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <p className="text-gray-900">{selectedBooking.user?.username} ({selectedBooking.user?.email})</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <p className="text-gray-900">{getConsultationTypeLabel(selectedBooking.consultation_type)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <p className="text-gray-900">{selectedBooking.title}</p>
              </div>
              {selectedBooking.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-900">{selectedBooking.description}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled</label>
                <p className="text-gray-900">
                  {new Date(selectedBooking.scheduled_at).toLocaleString()} ({selectedBooking.duration_minutes} min)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
                <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </span>
              </div>

              {selectedBooking.status === 'scheduled' && (
                <div className="border-t pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting URL (required to confirm)</label>
                  <input
                    type="url"
                    value={meetingUrl}
                    onChange={(e) => setMeetingUrl(e.target.value)}
                    placeholder="https://meet.google.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleConfirm(selectedBooking)}
                      disabled={updating || !meetingUrl.trim()}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg"
                    >
                      {updating ? 'Confirming...' : '✓ Confirm Booking'}
                    </button>
                    <button
                      onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                      disabled={updating}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {selectedBooking.status === 'confirmed' && (
                <div className="border-t pt-4 mt-4">
                  {selectedBooking.meeting_url && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meeting URL</label>
                      <a href={selectedBooking.meeting_url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-600 hover:underline break-all">{selectedBooking.meeting_url}</a>
                    </div>
                  )}
                  <button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'in_progress')}
                    disabled={updating}
                    className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg"
                  >
                    {updating ? 'Starting...' : '▶ Start Consultation'}
                  </button>
                </div>
              )}

              {selectedBooking.status === 'in_progress' && (
                <div className="border-t pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Notes</label>
                  <textarea
                    value={meetingNotes}
                    onChange={(e) => setMeetingNotes(e.target.value)}
                    rows={4}
                    placeholder="Add notes about the consultation..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={() => handleComplete(selectedBooking)}
                    disabled={updating}
                    className="w-full mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg"
                  >
                    {updating ? 'Completing...' : '✓ Mark as Completed'}
                  </button>
                </div>
              )}

              {(selectedBooking.status === 'completed' || selectedBooking.status === 'cancelled') && (
                <div className="border-t pt-4 mt-4 text-center text-gray-500">
                  This consultation has been {selectedBooking.status}.
                  {selectedBooking.meeting_notes && (
                    <div className="mt-2 text-left">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <p className="text-gray-900 bg-gray-50 p-3 rounded">{selectedBooking.meeting_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConsultationAdmin

